import { familyMemberIntegrationColumns } from './familyIntegrationFields';

const normalized = value => String(value || '').normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase().replace(/[^a-z]/g, '');
const familyAliases = { nome: 'name', parentesco: 'relationship', datadenascimento: 'birthDate', whatsapp: 'whatsapp', profissao: 'profession', escolaridade: 'educationLevel', renda: 'incomeCents', rendar: 'incomeCents', rendars: 'incomeCents', rendamensal: 'incomeCents', rendamensalr: 'incomeCents', rendamensalrs: 'incomeCents' };
const familyKeys = new Set(['name', 'relationship', 'birthDate', 'whatsapp', 'cpf', 'profession', 'educationLevel', 'incomeCents', 'livesWithStudent', 'livesWithAttendee']);
const columnKey = (column, family) => column.moduleBinding?.moduleField?.split('.').pop() || column.moduleField?.split('.').pop() || (family && (familyKeys.has(column.id) ? column.id : familyAliases[normalized(column.label)])) || column.id;
const bindingKey = field => field.moduleBinding?.moduleKey && field.moduleBinding?.moduleField
  ? `${field.moduleBinding.moduleKey}:${field.moduleBinding.moduleField}` : '';
const isFamily = field => {
  const binding = field.moduleBinding;
  if (binding?.moduleKey || binding?.moduleField) return ['students', 'attendees'].includes(binding.moduleKey) && binding.moduleField === 'family.members';
  return ['student_familyMembers', 'attendee_familyMembers'].includes(field.id);
};
function addColumns(existing = [], desired = [], family = false) {
  const result = [...existing];
  for (const column of desired) {
    if (result.some(current => columnKey(current, family) === columnKey(column, family))) continue;
    let id = column.id;
    while (result.some(current => current.id === id)) id += '_integration';
    result.push({ ...column, id });
  }
  return result;
}

// Hydrate old editor responses as well as freshly created integration templates.
export function hydrateIntegratedFamilyFields(fields = []) {
  return fields.map(field => {
    const binding = field.moduleBinding;
    const person = ['students', 'attendees'].includes(binding?.moduleKey);
    const legacy = /^(student|attendee)_responsible[12](Cpf|Whatsapp)$/.test(field.id);
    const moduleField = person ? binding.moduleField : (!binding?.moduleKey && legacy ? (field.id.endsWith('Cpf') ? 'family.responsible1.cpf' : 'family.responsible1.whatsapp') : '');
    if (/^family\.responsible[12]\.cpf$/.test(moduleField || '')) return { ...field, type: 'text', mask: 'cpf' };
    if (/^family\.responsible[12]\.whatsapp$/.test(moduleField || '')) return { ...field, type: 'phone', mask: 'phone' };
    if (field.type !== 'table' || !isFamily(field)) return field;
    const current = Array.isArray(field.tableColumns) ? field.tableColumns : [];
    const defaults = familyMemberIntegrationColumns();
    return { ...field, tableColumns: addColumns(current, current.length ? defaults.slice(4) : defaults, true) };
  });
}

export function updateModuleIntegration({ fields = [], groups = [], integration = null }, template, mode) {
  if (!['missing', 'replace'].includes(mode)) throw new Error('Modo de atualização inválido.');
  const desired = hydrateIntegratedFamilyFields(template.fields || []);
  if (mode === 'replace') return { fields: desired.map(field => ({ ...field })), groups: (template.groups || []).map(group => ({ ...group })), integration: { ...template.integration } };
  const result = hydrateIntegratedFamilyFields(fields);
  const nextGroups = [...groups];
  const matches = new Map(desired.map(field => [field.id, result.find(current => {
    const key = bindingKey(field), currentKey = bindingKey(current);
    return key && currentKey ? key === currentKey : current.id === field.id;
  })]));
  const groupIds = new Map();
  for (const group of template.groups || []) {
    let existing = groups.find(current => current.id === group.id) || groups.find(current => normalized(current.label) === normalized(group.label));
    if (!existing) {
      const matchedGroupIds = [...new Set(desired.filter(field => field.groupId === group.id).map(field => matches.get(field.id)?.groupId).filter(Boolean))];
      if (matchedGroupIds.length === 1) existing = groups.find(current => current.id === matchedGroupIds[0]);
    }
    if (existing) groupIds.set(group.id, existing.id);
    else { nextGroups.push({ ...group }); groupIds.set(group.id, group.id); }
  }
  const ids = new Map();
  const addedIds = new Set();
  for (const field of desired) {
    const existing = matches.get(field.id);
    if (existing) {
      ids.set(field.id, existing.id);
      const index = result.findIndex(current => current.id === existing.id);
      result[index] = { ...existing, ...(!existing.moduleBinding && field.moduleBinding ? { moduleBinding: { ...field.moduleBinding } } : {}),
        ...(existing.type === 'table' && field.type === 'table' ? { tableColumns: addColumns(existing.tableColumns || [], field.tableColumns || [], isFamily(field)) } : {}) };
    } else {
      let id = field.id;
      while (result.some(current => current.id === id)) id += '_integration';
      ids.set(field.id, id);
      addedIds.add(id);
      result.push({ ...field, id, groupId: groupIds.get(field.groupId) || field.groupId });
    }
  }
  for (const field of result) {
    if (!addedIds.has(field.id) || !field.visibleWhen) continue;
    // Match the visibility evaluator's identifiers; quoted answer values stay literal.
    field.visibleWhen = field.visibleWhen.replace(/"(?:\\.|[^"\\])*"|'(?:\\.|[^'\\])*'|[A-Za-z_$][A-Za-z0-9_$.-]*/g, token => {
      if (token.startsWith('"') || token.startsWith("'")) return token;
      return ids.get(token) || (token.startsWith('values.') && ids.has(token.slice(7)) ? 'values.' + ids.get(token.slice(7)) : token);
    });
  }
  const sameModule = integration?.moduleKey === template.integration?.moduleKey;
  const required = (template.integration?.requiredFieldIds || []).map(id => ids.get(id) || id);
  return { fields: result, groups: nextGroups, integration: {
    ...template.integration, ...(sameModule ? integration : {}),
    requiredFieldIds: [...new Set([...(sameModule ? integration.requiredFieldIds || [] : []), ...required])],
  } };
}
